import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Form } from "react-bootstrap";
import {
  CheckCircle2,
  XCircle,
  Link as LinkIcon,
  Unlink,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import { AppButton, ConfirmModal } from "../../ui";
import apiClient from "../../../utils/apiClient";

const ZOHO_REGIONS = [
  { value: "in", label: "India (.in)" },
  { value: "com", label: "US (.com)" },
  { value: "eu", label: "EU (.eu)" },
  { value: "au", label: "Australia (.com.au)" },
  { value: "jp", label: "Japan (.jp)" },
];

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const PlatformAccountPanel = ({ showToast, defaultRegion = "in" }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null);
  const [region, setRegion] = useState(defaultRegion);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    const { data } = await apiClient.get("/sa/zoho-meeting/platform-account");
    setState(data);
    if (data?.region) setRegion(data.region);
  }, []);

  useEffect(() => {
    (async () => {
      try { await refresh(); } catch { /* silent */ }
      setLoading(false);
    })();
    // Refresh after returning from OAuth flow
    const url = new URL(window.location.href);
    if (url.searchParams.get("platform_connected")) {
      url.searchParams.delete("platform_connected");
      window.history.replaceState({}, "", url.toString());
      showToast?.("Platform Zoho account connected!", "success");
    }
  }, [refresh, showToast]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.get("/sa/zoho-meeting/platform-account/oauth-url", {
        params: { region },
      });
      window.location.href = data.url;
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to start OAuth.", "error");
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      await apiClient.delete("/sa/zoho-meeting/platform-account");
      await refresh();
      showToast?.("Platform Zoho account disconnected.", "success");
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to disconnect.", "error");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const connected = !!state?.connected;

  return (
    <div className="zoho-platform-account">
      <div className="zoho-platform-account__status">
        {connected ? (
          <>
            <CheckCircle2 size={18} className="zoho-platform-account__icon zoho-platform-account__icon--ok" />
            <div>
              <div className="zoho-platform-account__title">Connected</div>
              <div className="zoho-platform-account__sub">
                {state.ownerEmail} · region <code>.{state.region}</code>
              </div>
            </div>
          </>
        ) : (
          <>
            <XCircle size={18} className="zoho-platform-account__icon zoho-platform-account__icon--off" />
            <div>
              <div className="zoho-platform-account__title">Not connected</div>
              <div className="zoho-platform-account__sub">
                Connect a Zoho Meeting account that LMS Campus will use to host classes for tenants on the
                <strong> Platform-hosted Zoho</strong> mode.
              </div>
            </div>
          </>
        )}
      </div>

      <div className="zoho-platform-account__meta">
        <div><span>Connected at</span><strong>{fmtDate(state?.connectedAt)}</strong></div>
        <div><span>Token expires</span><strong>{fmtDate(state?.expiresAt)}</strong></div>
        <div><span>Last recording poll</span><strong>{fmtDate(state?.lastRecordingPollAt)}</strong></div>
        <div><span>API domain</span><strong>{state?.apiDomain || "—"}</strong></div>
      </div>

      <div className="zoho-platform-account__actions">
        {!connected && (
          <Form.Group className="zoho-platform-account__region">
            <Form.Label className="sa-settings__label">Region</Form.Label>
            <Form.Select size="sm" value={region} onChange={(e) => setRegion(e.target.value)}>
              {ZOHO_REGIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {connected ? (
          <>
            <AppButton variant="secondary" size="sm" onClick={refresh} disabled={busy}>
              <RefreshCw size={14} className="me-1" /> Refresh
            </AppButton>
            <AppButton variant="danger" size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
              <Unlink size={14} className="me-1" /> Disconnect
            </AppButton>
          </>
        ) : (
          <AppButton variant="primary" size="sm" onClick={handleConnect} disabled={busy}>
            <LinkIcon size={14} className="me-1" />
            {busy ? "Redirecting…" : "Connect Zoho Account"}
            <ExternalLink size={12} className="ms-1" />
          </AppButton>
        )}
      </div>

      <ConfirmModal
        show={confirmOpen}
        title="Disconnect platform Zoho account?"
        message="Institutes on Platform-hosted Zoho mode will be unable to start new classes until you reconnect. Active classes are not interrupted."
        confirmText="Disconnect"
        variant="danger"
        onConfirm={handleDisconnect}
        onHide={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PlatformAccountPanel;
